import { gsap, ScrollTrigger } from '../lib/gsap';
import { prefersReducedMotion } from '../lib/lenis';

// Background scroll companion: a giant sharp mark centred behind the page.
// Its outline draws itself across the full scroll and the glyph fills faintly
// over the closing stretch — finish the page, complete the mark.
//
// Choreography (all scrubbed, all gentle): it breathes slightly larger and
// settles its tilt through the features section, bows out just before the
// opaque sticky panels would flicker over it, then re-enters at the
// testimonials in a fresh pose — a quiet scene change — and finishes upright
// and a touch larger behind the closing CTA.
export function initScrollMark(): void {
  const root = document.querySelector<HTMLElement>('[data-scroll-mark]');
  const outline = document.querySelector<SVGPathElement>('[data-scroll-mark-outline]');
  const fill = document.querySelector<SVGPathElement>('[data-scroll-mark-fill]');
  if (!root || !outline) return;

  const len = outline.getTotalLength();

  if (prefersReducedMotion()) {
    // Static, fully drawn — present but silent.
    gsap.set(outline, { strokeDasharray: len, strokeDashoffset: 0 });
    return;
  }

  gsap.set(outline, { strokeDasharray: len, strokeDashoffset: len });
  // Hidden through the hero — the companion joins only at the app frame.
  gsap.set(root, { xPercent: -50, yPercent: -50, rotate: -8, scale: 0.86, autoAlpha: 0 });

  // ── The line draws from the app frame down to the end of the page ────────
  const draw = gsap.timeline({
    scrollTrigger: {
      trigger: '.app-frame-section',
      start: 'top 90%',
      endTrigger: document.body,
      end: 'bottom bottom',
      scrub: 0.6,
    },
  });
  draw.to(outline, { strokeDashoffset: 0, ease: 'none', duration: 0.85 }, 0);
  if (fill) {
    draw.to(fill, { opacity: 0.015, ease: 'none', duration: 0.15 }, 0.85);
  }

  const scrub = { scrub: 0.6 };
  const frame = document.querySelector<HTMLElement>('.app-frame-section');
  const stack = document.querySelector<HTMLElement>('[data-sticky-stack]');
  const testimonials = document.querySelector<HTMLElement>('.testimonials');
  const cta = document.querySelector<HTMLElement>('.cta-banner');

  // ── Entrance: fade in as the app frame scrolls into view. ────────────────
  if (frame) {
    gsap.fromTo(root,
      { autoAlpha: 0 },
      {
        autoAlpha: 1,
        ease: 'none',
        immediateRender: false,
        scrollTrigger: { trigger: frame, start: 'top 85%', end: 'top 35%', ...scrub },
      }
    );
  }

  // ── Act I: app frame → features. Breathe up, ease the tilt. ──────────────
  gsap.fromTo(root,
    { rotate: -8, scale: 0.86 },
    {
      rotate: -4,
      scale: 1,
      ease: 'none',
      scrollTrigger: { trigger: '#features', start: 'top bottom', end: 'bottom center', ...scrub },
    }
  );

  // ── Exit before the sticky stack (its panels are opaque and would strobe
  //    the mark during their cross-fades). ──────────────────────────────────
  if (stack) {
    gsap.fromTo(root,
      { autoAlpha: 1 },
      {
        autoAlpha: 0,
        ease: 'none',
        immediateRender: false,
        scrollTrigger: { trigger: stack, start: 'top 65%', end: 'top 15%', ...scrub },
      }
    );
  }

  // ── Act II: re-enter at the testimonials in a new pose — tilted the other
  //    way, a touch larger. The pose jump happens while invisible. ─────────
  if (testimonials) {
    gsap.fromTo(root,
      { autoAlpha: 0, rotate: 6, scale: 1.0 },
      {
        autoAlpha: 1,
        rotate: 3,
        scale: 1.06,
        ease: 'none',
        immediateRender: false,
        scrollTrigger: { trigger: testimonials, start: 'top 90%', end: 'top 35%', ...scrub },
      }
    );
  }

  // ── Finale: settle upright, slightly grand, as the fill blooms. ──────────
  if (cta) {
    gsap.fromTo(root,
      { rotate: 3, scale: 1.06 },
      {
        rotate: 0,
        scale: 1.14,
        ease: 'none',
        immediateRender: false,
        scrollTrigger: { trigger: cta, start: 'top bottom', end: 'bottom 60%', ...scrub },
      }
    );
  }

  window.setTimeout(() => ScrollTrigger.refresh(), 600);
}
